/**
 * Simple TipTap/ProseMirror JSON to HTML serializer.
 * Avoids @tiptap/html peer dependency on happy-dom.
 */

interface ProseMirrorNode {
  type: string
  content?: ProseMirrorNode[]
  text?: string
  marks?: Array<{ type: string; attrs?: Record<string, unknown> }>
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
}

/** NOS-187: link marks carry untrusted imported hrefs — allow only safe schemes. */
const SAFE_HREF_RE = /^(https?:\/\/|mailto:|tel:|\/)/i

function safeHref(attrs: Record<string, unknown> | undefined): string | null {
  const href = attrs?.href
  if (typeof href !== "string") return null
  const trimmed = href.trim()
  return SAFE_HREF_RE.test(trimmed) ? trimmed : null
}

function nodeToHtml(node: ProseMirrorNode): string {
  if (node.type === "text") {
    let text = escapeHtml(node.text ?? "")
    const marks = node.marks ?? []
    for (const m of marks) {
      if (m.type === "bold") text = `<strong>${text}</strong>`
      else if (m.type === "italic") text = `<em>${text}</em>`
      else if (m.type === "code") text = `<code>${text}</code>`
      else if (m.type === "link") {
        const href = safeHref(m.attrs)
        if (href) text = `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${text}</a>`
      }
    }
    return text
  }

  const children = (node.content ?? [])
    .map((c) => nodeToHtml(c))
    .join("")

  switch (node.type) {
    case "doc":
      return children
    case "paragraph":
      return children ? `<p>${children}</p>` : "<p></p>"
    case "heading":
      const level = (node as { attrs?: { level?: number } }).attrs?.level ?? 1
      return `<h${level}>${children}</h${level}>`
    case "bulletList":
      return `<ul>${children}</ul>`
    case "orderedList":
      return `<ol>${children}</ol>`
    case "listItem":
      return `<li>${children}</li>`
    case "blockquote":
      return `<blockquote>${children}</blockquote>`
    case "codeBlock":
      return `<pre><code>${children}</code></pre>`
    case "hardBreak":
      return "<br>"
    case "horizontalRule":
      return "<hr>"
    default:
      return children
  }
}

/** Convert TipTap/ProseMirror JSON to HTML string */
export function tiptapJsonToHtml(doc: unknown): string {
  if (!doc || typeof doc !== "object") return ""
  const node = doc as ProseMirrorNode
  return nodeToHtml(node)
}

/** Extract plain text from TipTap/ProseMirror JSON (for excerpts) */
function nodeToPlainText(node: ProseMirrorNode): string {
  if (node.type === "text") {
    return node.text ?? ""
  }
  const children = (node.content ?? []).map((c) => nodeToPlainText(c)).join(" ")
  return children
}

/** Convert TipTap/ProseMirror JSON to plain text string */
export function tiptapJsonToPlainText(doc: unknown): string {
  if (!doc || typeof doc !== "object") return ""
  const node = doc as ProseMirrorNode
  return nodeToPlainText(node).replace(/\s+/g, " ").trim()
}
